<template>
  <scroll-layout>
    <template slot="header">
      <ykc-search
        ref="YkcSearch"
        :data="searchData"
        :searchForm="searchParams"
        @click="handleSearchConfirm"
        @clean="handleSearchClean"></ykc-search>
    </template>
    <div class="merchant-intention">
      <ykc-table
        ref="YkcTable"
        :data="tableData"
        :columns="tableColumns"
        :title="tableTitle"
        operateFixedType="right"
        :operateButtons="tableOperateButtons"
        @sort-change="handleSortChange">
        <template #headerButtons>
          <div class="table-header-button">
            <template v-for="(btn, i) in tableButtons">
              <ykc-button :type="btn.type" v-if="btn.enabled()" :key="i" @click="btn.handler">
                {{ btn.label }}
              </ykc-button>
            </template>
          </div>
        </template>
        <ykc-pagination
          slot="pagination"
          ref="YkcPagination"
          :total="pageInfo.total"
          :current-page.sync="pageInfo.current"
          @current-change="handlePaginationCurrentChange"
          :page-size.sync="pageInfo.size"
          @size-change="handlePaginationSizeChange"></ykc-pagination>
      </ykc-table>
    </div>
    <ykc-drawer
      :show.sync="showDrawer"
      :title="drawerTitle"
      :on-close="onAddDrawerClose"
      :before-cancel="onAddDrawerCancel"
      ensure-txt="保存">
      <template #footer>
        <div class="drawer-button-wrap">
          <template v-for="(btn, i) in drawerButtons">
            <ykc-button
              :type="btn.type"
              v-if="btn.enabled()"
              :disabled="btn.disabled ? btn.disabled() : false"
              :loading="btn.disabled ? btn.disabled() : false"
              :key="i"
              @click="btn.handler">
              {{ btn.label }}
            </ykc-button>
          </template>
        </div>
      </template>
      <div class="drawer-main-body" v-if="showDrawer">
        <AddOrEdit ref="addOrEdit" :data="acceptRow"></AddOrEdit>
      </div>
    </ykc-drawer>
    <ykc-operation-dialog
      title="驳回原因"
      confirm-btn-txt="确认"
      :show.sync="optionReject"
      :before-confirm="rejectConfirm">
      <div slot="content">
        <div class="reject-name">合作申请方名称：{{ rejectRow.name }}</div>
        <el-input
          type="textarea"
          placeholder="请输入驳回原因"
          :autosize="{ minRows: 4 }"
          v-model="rejectReason"></el-input>
      </div>
    </ykc-operation-dialog>
  </scroll-layout>
</template>

<script>
  import YkcDialog from '@/components/ykc-ui/dialog/index';
  import AddOrEdit from './AddOrEdit.vue';
  import { operatorData } from '@/service/apis';
  import { offlineExport, code } from '@/utils';

  export default {
    name: 'MerchantIntentionList',
    components: {
      AddOrEdit,
    },
    data() {
      return {
        drawerType: '',
        optionReject: false,
        rejectRow: {},
        acceptRow: {},
        rejectReason: '',
        drawerButtons: [
          {
            label: '取消',
            type: 'plain',
            enabled: () => true,
            handler: () => {
              YkcDialog({
                dialogType: 'feedback',
                showTitle: false,
                showFooter: true,
                desc: '取消后操作将不被保存，确认是否取消？',
                onCancel: dialogDone => {
                  dialogDone(); // 隐藏弹窗
                },
                onConfirm: dialogDone => {
                  dialogDone(); // 隐藏弹窗
                  this.showDrawer = false; // 隐藏抽屉
                },
              });
            },
          },
          {
            label: '保存',
            enabled: () => true,
            disabled: () => {
              return this.isRequest;
            },
            handler: () => {
              if (this.isRequest) return;
              this.isRequest = true;
              this.$refs.addOrEdit
                .submitForm()
                .then(() => {
                  this.showDrawer = false;
                  this.isRequest = false;
                  this.searchTableList();
                })
                .finally(() => {
                  this.isRequest = false;
                });
            },
          },
        ],
        tableButtons: [
          {
            label: '导出',
            type: 'plain',
            enabled: () => code('merchant:intention:export'),
            handler: () => {
              this.doExportFile();
            },
          },
        ],
        tableData: [],

        tableColumns: [
          { label: '合作意向编码', prop: 'intentionCode', minWidth: '150px' },
          { label: '合作申请方名称', prop: 'name', minWidth: '100px' },
          { label: '拟加盟城市', prop: 'cityName', minWidth: '100px' },
          {
            label: '意向提交时间',
            prop: 'createdTime',
            minWidth: '150px',
            sortable: 'custom',
            scopedSlots: {
              default: ({ row }) => {
                const dateStr = row.createdTime
                  ? row.createdTime.slice(0, 10).replace(/-/g, '.')
                  : '';
                return (
                  <div class="table-date">
                    <span>{dateStr}</span>
                    {row.isOvertime === 1 && <span class="table-date-overtime"> 已超时</span>}
                  </div>
                );
              },
            },
          },
          {
            label: '审核状态',
            prop: 'status',
            minWidth: '100px',
            scopedSlots: {
              default: ({ row }) => {
                let statusStr = '';

                if (row.status === 0) {
                  statusStr = '待审核';
                } else if (row.status === 1) {
                  statusStr = '审核通过';
                } else if (row.status === 2) {
                  statusStr = '审核驳回';
                }

                return <span>{statusStr}</span>;
              },
            },
          },
          { label: '联系人姓名', prop: 'contactName', minWidth: '100px' },
          { label: '联系人手机号', prop: 'mobile', minWidth: '100px' },
          { label: '联系人职务', prop: 'job', minWidth: '100px' },
          { label: '负责人', prop: 'assigneeName', minWidth: '100px' },
          { label: '驳回原因', prop: 'rejectReason', minWidth: '120px' },
          { label: '关联平台商户', prop: 'partnerName', minWidth: '120px' },
        ],
        tableTitle: '合作意向列表',

        tableOperateButtons: [
          {
            render: (h, data, { row }) =>
              code('merchant:intention:audit') && row.status === 0 ? (
                <ykc-button
                  type="text"
                  onClick={() => {
                    this.acceptRow = row;
                    this.showDrawer = true;
                  }}>
                  通过
                </ykc-button>
              ) : null,
          },
          {
            render: (h, data, { row }) =>
              code('merchant:intention:reject') && row.status === 0 ? (
                <ykc-button
                  type="text"
                  onClick={() => {
                    this.handleReject(row);
                  }}>
                  驳回
                </ykc-button>
              ) : null,
          },
        ],
        pageInfo: {
          total: 0,
          current: 1,
          size: 10,
        },
        searchParams: {
          intentionCode: '', // 平台商户
          name: '',
          contactName: '',
          status: '',
          isOvertime: '',
          submitDate: '',
          partnerName: '',
          sortField: 'createdTime',
          sortOrder: 'asc',
        },
        showDrawer: false,
        isRequest: false,
      };
    },
    created() {
      this.searchTableList();
    },
    computed: {
      drawerTitle() {
        return '请完善合作方信息';
      },
      searchData() {
        return [
          {
            comName: 'YkcInput',
            key: 'intentionCode',
            label: '合作意向编码',
            placeholder: '请输入合作意向编码',
          },
          {
            comName: 'YkcInput',
            key: 'name',
            label: '合作申请方名称',
            placeholder: '请输入合作申请方名称',
          },
          {
            comName: 'YkcInput',
            key: 'contactName',
            label: '联系人姓名',
            placeholder: '请输入联系人姓名',
          },
          {
            comName: 'YkcDropdown',
            key: 'status',
            label: '审核状态',
            placeholder: '请选择审核状态',
            data: [
              { id: '', name: '全部' },
              { id: '0', name: '待审核' },
              { id: '1', name: '审核通过' },
              { id: '2', name: '审核驳回' },
            ],
          },
          {
            comName: 'YkcDropdown',
            key: 'isOvertime',
            label: '是否超时',
            placeholder: '请选择是否超时',
            data: [
              { id: '', name: '全部' },
              { id: '0', name: '否' },
              { id: '1', name: '是' },
            ],
          },
          {
            comName: 'YkcDatePicker',
            key: 'submitDate',
            label: '意向提交时间',
          },
          {
            comName: 'YkcInput',
            key: 'partnerName',
            label: '关联商户',
            placeholder: '请输入关联商户名称',
          },
        ];
      },
    },
    methods: {
      handleReject(row) {
        this.optionReject = true;
        this.rejectRow = row;
        this.rejectReason = '';
      },
      /**
       * 拒绝确认
       */
      async rejectConfirm() {
        if (!this.rejectReason || !this.rejectReason.trim()) {
          this.$message.warning('请输入驳回原因');
          return false;
        }
        try {
          await operatorData.auditList({
            intentionId: this.rejectRow.id,
            status: 2,
            rejectReason: this.rejectReason,
          });
          this.$message({
            message: '操作成功',
            type: 'success',
          });
          this.optionReject = false;
          this.searchTableList();
          return true;
        } catch {
          return false;
        }
      },
      /** 根据条件查询数据 */
      searchTableList() {
        const data = {
          ...this.pageInfo,
          ...this.searchParams,
        };
        if (Array.isArray(data.submitDate) && data.submitDate.length === 2) {
          [data.submitStartDate, data.submitEndDate] = data.submitDate;
          delete data.submitDate;
        }
        Promise.all([operatorData.intentionList(data)]).then(([res1]) => {
          this.tableData = res1?.records;
          this.pageInfo.total = res1?.total || 0;
        });
      },
      /**
       * @desc 导出文件 - 平台商户合作意向列表
       * */
      doExportFile() {
        const data = {
          ...this.searchParams,
        };
        if (Array.isArray(data.submitDate) && data.submitDate.length === 2) {
          [data.submitStartDate, data.submitEndDate] = data.submitDate;
          delete data.submitDate;
        }
        offlineExport(
          {
            downloadType: 'cooperation_intention_export',
            jsonNode: data,
          },
          this.pageInfo.total
        );
      },
      onAddDrawerClose(done) {
        done();
      },
      /**
       * @desc 点击抽屉取消按钮 callback
       * */
      onAddDrawerCancel(drawerDone) {
        YkcDialog({
          dialogType: 'feedback',
          showTitle: false,
          showFooter: true,
          desc: '取消后操作将不被保存，确认是否取消？',
          onCancel: done => {
            done();
          },
          onConfirm: done => {
            done();
            drawerDone();
            if (this.isEdit) {
              this.isEdit = false;
            }
          },
        });
      },
      /**
       * @desc 点击查询按钮
       * */
      handleSearchConfirm(form) {
        this.pageInfo.current = 1;
        Object.assign(this.searchParams, form);
        this.searchTableList();
      },

      /**
       * @desc 表格排序切换
       * */
      handleSortChange({ prop, order }) {
        if (prop === 'createdTime') {
          this.searchParams.sortField = 'createdTime';
          this.searchParams.sortOrder = order === 'descending' ? 'desc' : 'asc';
        } else {
          this.searchParams.sortField = 'createdTime';
          this.searchParams.sortOrder = 'asc';
        }
        this.pageInfo.current = 1;
        this.searchTableList();
      },
      /**
       * 点击清空查询按钮
       * */
      handleSearchClean(form) {
        Object.assign(this.searchParams, form);
      },
      /**
       * @desc 分页 change 事件监听器
       * */
      handlePaginationCurrentChange(current) {
        this.pageInfo.current = current;
        this.searchTableList();
      },
      /**
       * 分页 size  change  事件监听器
       * */
      handlePaginationSizeChange(size) {
        this.pageInfo.current = 1;
        this.pageInfo.size = size;
        this.searchTableList();
      },
    },
  };
</script>
<style lang="scss" scoped>
  .link {
    color: blue;
    cursor: pointer;
  }
  // 表头 label 与排序箭头同行显示（ykc-table 表头插槽为块级元素，会把排序箭头挤到下一行并被裁剪隐藏）
  .merchant-intention ::v-deep .ykc-table-label-wrap {
    display: inline-block;
  }
  .el-radio-group .item {
    border: 0;
  }
  .table-date {
    .table-date-overtime {
      color: red;
    }
  }
  .reject-name {
    margin-bottom: 12px;
    color: #666;
  }
</style>
